import { useEffect, useRef, useState } from "react";
import { Camera as CameraIcon, Search as SearchIcon } from "@tamagui/lucide-icons";
import { Body2, Body3, Button, isWeb, Subtitle2, Subtitle3, shadow, Theme, useMedia, YStack } from "@medbillai/ui";
import { createParam } from "@medbillai/navigation/params";
import { useRouter } from "@medbillai/navigation/router";
import { Flag } from "@medbillai/utils";
import { defaultWidthLimit, ScreenView } from "../../../components/ScreenView";
import { analyticsService } from "../../../lib/analytics";
import { useBoolVariation } from "../../../lib/flag";
import { SingularEvents, singularService } from "../../../lib/singular";
import { usePushGetHelp } from "../../../lib/usePushGetHelp";
import { EmptyBillList } from "../components/empty-bill-list/EmptyBillList";
import { useInsuranceLinkStatus } from "../utils/useNormalizedEobs";
import { FilterChips } from "./FilterChips";
import { RecordRow } from "./RecordRow";
import { countNeedingYou, groupByMonth, peopleIn, type RecordFilters, type RecordItem, recordUrl, visibleRecords } from "./records";
import { SearchField } from "./SearchField";
import { useRecords } from "./useRecords";
import { useTabBarInset } from "./useTabBarInset";
const {
  useParam
} = createParam<{
  id: string;
}>();
const Dock = ({
  children,
  inset = 0,
  onHeight
}: {
  children: React.ReactNode;
  /** Space under the button that something else covers, such as a tab bar. */
  inset?: number;
  onHeight: (height: number) => void;
}) => <YStack testID="bills-claims.dock" position="absolute" left={0} right={0} bottom={0} padding="$sp2" maxWidth={defaultWidthLimit} $platform-web={{
  marginHorizontal: "auto"
}} pointerEvents="box-none" onLayout={event => onHeight(event.nativeEvent.layout.height)} data-sentry-element="YStack" data-sentry-component="Dock" data-sentry-source-file="BillsClaimsScreen.tsx">
    <YStack borderRadius="$brFull" pointerEvents="auto" {...shadow.medium} data-sentry-element="YStack" data-sentry-source-file="BillsClaimsScreen.tsx">
      {children}
    </YStack>
    <YStack height={inset} data-sentry-element="YStack" data-sentry-source-file="BillsClaimsScreen.tsx" />
  </YStack>;
const AddBillButton = ({
  onHeight
}: {
  onHeight: (height: number) => void;
}) => {
  const {
    pushGetHelp
  } = usePushGetHelp();
  const tabBarInset = useTabBarInset();
  return <Dock inset={tabBarInset} onHeight={onHeight} data-sentry-element="Dock" data-sentry-component="AddBillButton" data-sentry-source-file="BillsClaimsScreen.tsx">
      <Button variant="brand" size="large" iconLeft={CameraIcon} onPress={() => pushGetHelp({
      caseType: "BILL_ISSUE_V2",
      title: "Add a bill",
      source: "bills_claims_list"
    })} data-sentry-element="Button" data-sentry-source-file="BillsClaimsScreen.tsx">
        Add a bill
      </Button>
    </Dock>;
};
const NothingHere = ({
  searching,
  filters
}: {
  searching: boolean;
  filters: RecordFilters;
}) => {
  const {
    status,
    person
  } = filters;
  // Per filter change, not per keystroke: typing on in an empty search reports once.
  useEffect(() => {
    analyticsService.trackEvent("BillsClaimsNothingFound", {
      searching,
      status,
      byPerson: person !== undefined
    });
  }, [searching, status, person]);
  return <YStack alignItems="center" paddingTop="$sp3" paddingBottom="$sp5" data-sentry-element="YStack" data-sentry-component="NothingHere" data-sentry-source-file="BillsClaimsScreen.tsx">
      <SearchIcon color="$iconSubtle" data-sentry-element="SearchIcon" data-sentry-source-file="BillsClaimsScreen.tsx" />
      <Subtitle2 marginTop="$sp1" data-sentry-element="Subtitle2" data-sentry-source-file="BillsClaimsScreen.tsx">Nothing here</Subtitle2>
      <Body3 marginTop="$sp0.5" color="$textSubtle" data-sentry-element="Body3" data-sentry-source-file="BillsClaimsScreen.tsx">
        Try a different filter or search.
      </Body3>
    </YStack>;
};
export const LoadError = ({
  message,
  onRetry
}: {
  message: string;
  onRetry: () => void;
}) => <YStack gap="$sp1" paddingHorizontal="$sp2" alignItems="flex-start" data-sentry-element="YStack" data-sentry-component="LoadError" data-sentry-source-file="BillsClaimsScreen.tsx">
    <Body2 color="$textSubtle" data-sentry-element="Body2" data-sentry-source-file="BillsClaimsScreen.tsx">{message}</Body2>
    <Button variant="secondary" size="small" onPress={onRetry} data-sentry-element="Button" data-sentry-source-file="BillsClaimsScreen.tsx">
      Try again
    </Button>
  </YStack>;
const MonthGroups = ({
  records,
  selectedKey,
  onOpen
}: {
  records: RecordItem[];
  selectedKey?: string;
  onOpen: (record: RecordItem) => void;
}) => <YStack gap="$sp2" paddingHorizontal="$sp2" testID="bills-claims.list" data-sentry-element="YStack" data-sentry-component="MonthGroups" data-sentry-source-file="BillsClaimsScreen.tsx">
    {groupByMonth(records).map(group => <YStack key={group.month} gap="$sp1">
        <Subtitle3 color="$textSubtle" paddingHorizontal="$sp0.5">
          {group.month}
        </Subtitle3>
        {group.records.map(record => <RecordRow key={`${record.kind}:${record.key}`} record={record} selected={record.key === selectedKey} onPress={() => onOpen(record)} />)}
      </YStack>)}
  </YStack>;
const useListViewed = (loading: boolean, recordCount: number, loadError: "some" | "all" | undefined) => {
  const viewed = useRef(false);
  useEffect(() => {
    if (loading || viewed.current) {
      return;
    }
    viewed.current = true;
    analyticsService.trackEvent("BillsClaimsListViewed", {
      recordCount,
      ...(loadError ? {
        loadError
      } : {})
    });
  }, [loading, recordCount, loadError]);
};
const useSelectFirstRecord = (enabled: boolean, selectedKey: string | undefined, loading: boolean, first: RecordItem | undefined) => {
  const media = useMedia();
  const {
    replace
  } = useRouter();
  const selected = useRef(false);
  useEffect(() => {
    if (selectedKey) {
      selected.current = false;
      return;
    }
    if (selected.current || !enabled || !isWeb || !media.gtMd || loading || !first) {
      return;
    }
    selected.current = true;
    replace(recordUrl(first));
  }, [enabled, media.gtMd, selectedKey, loading, first, replace]);
};
export const BillsClaimsScreen = ({
  selectFirst = false
}: {
  selectFirst?: boolean;
}) => {
  const [selectedKey] = useParam("id");
  const {
    push
  } = useRouter();
  const [filters, setFilters] = useState<RecordFilters>({
    status: "all"
  });
  const [query, setQuery] = useState("");
  const [dockHeight, setDockHeight] = useState(0);
  const searched = useRef(false);
  const {
    records,
    loading,
    error,
    incomplete,
    refetch
  } = useRecords();
  const {
    dataInsuranceLinks
  } = useInsuranceLinkStatus();
  const personFilter = useBoolVariation(Flag.BILLS_PERSON_FILTER, false);
  useListViewed(loading, records.length, !error ? undefined : records.length === 0 ? "all" : incomplete ? "some" : undefined);
  const filterBy = (change: Partial<RecordFilters>) => {
    const next = {
      ...filters,
      ...change
    };
    setFilters(next);
    analyticsService.trackEvent("BillsClaimsFilterSelected", {
      status: next.status,
      byPerson: next.person !== undefined
    });
  };
  const search = (text: string) => {
    setQuery(text);
    if (text && !searched.current) {
      searched.current = true;
      analyticsService.trackEvent("BillsClaimsSearched", {});
    }
  };
  const open = (record: RecordItem) => {
    singularService.event(SingularEvents.openBill);
    analyticsService.trackEvent("BillsClaimsRecordOpened", {
      kind: record.kind,
      status: record.status
    });
    push(recordUrl(record));
  };
  const retry = () => void refetch().catch(() => undefined);
  const shown = visibleRecords(records, filters, query);
  useSelectFirstRecord(selectFirst, selectedKey, loading, shown[0]);
  return <YStack f={1} backgroundColor="$surface1" data-sentry-element="YStack" data-sentry-component="BillsClaimsScreen" data-sentry-source-file="BillsClaimsScreen.tsx">
      <ScreenView noPadding centerHorizontal onRefresh={refetch}
    // Unbounded, it grows past the web Bills pane, which never scrolls.
    scrollViewProps={{
      f: 1,
      contentInsetAdjustmentBehavior: "automatic"
    }} data-sentry-element="ScreenView" data-sentry-source-file="BillsClaimsScreen.tsx">
        <YStack gap="$sp2" paddingTop="$sp2" paddingBottom={dockHeight} data-sentry-element="YStack" data-sentry-source-file="BillsClaimsScreen.tsx">
          <Theme name="v2" data-sentry-element="Theme" data-sentry-source-file="BillsClaimsScreen.tsx">
            <YStack paddingHorizontal="$sp2" data-sentry-element="YStack" data-sentry-source-file="BillsClaimsScreen.tsx">
              <SearchField label="Search bills and claims" value={query} onChangeText={search} data-sentry-element="SearchField" data-sentry-source-file="BillsClaimsScreen.tsx" />
            </YStack>
            <FilterChips filters={filters} onStatusChange={status => filterBy({
            status
          })} needsYouCount={countNeedingYou(records, filters)} people={personFilter ? peopleIn(records) : []} onPersonChange={person => filterBy({
            person
          })} data-sentry-element="FilterChips" data-sentry-source-file="BillsClaimsScreen.tsx" />
          </Theme>
          {records.length === 0 && !error ? <EmptyBillList filter="all" insuranceLinks={dataInsuranceLinks} loading={loading} /> : <Theme name="v2">
              {records.length === 0 ? <LoadError message="We couldn’t load your bills and claims." onRetry={retry} /> : <>
                  {incomplete ? <LoadError message="Some of your bills and claims couldn’t load." onRetry={retry} /> : null}
                  {shown.length === 0 ? <NothingHere searching={query !== ""} filters={filters} /> : <MonthGroups records={shown} selectedKey={selectedKey} onOpen={open} />}
                </>}
            </Theme>}
        </YStack>
      </ScreenView>
      <Theme name="v2" data-sentry-element="Theme" data-sentry-source-file="BillsClaimsScreen.tsx">
        <AddBillButton onHeight={setDockHeight} data-sentry-element="AddBillButton" data-sentry-source-file="BillsClaimsScreen.tsx" />
      </Theme>
    </YStack>;
};